import { z } from 'zod';

export const dealStatuses = ['PROPOSAL', 'WON', 'LOST'] as const;
export type DealStatus = (typeof dealStatuses)[number];

export const dealListQuerySchema = z.object({
  status: z.enum(dealStatuses).optional(),
  stageId: z.string().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type DealListQuery = z.infer<typeof dealListQuerySchema>;

export const dealSummarySchema = z.object({
  id: z.string(),
  leadId: z.string(),
  status: z.enum(dealStatuses),
  title: z.string(),
  stageId: z.string(),
  stageName: z.string(),
  stageActive: z.boolean(),
  amount: z.string(),
  currency: z.string(),
  probability: z.number().int().min(0).max(100),
  expectedCloseDate: z.string().nullable(),
  serviceKey: z.string().nullable(),
  marketCode: z.string().nullable(),
  version: z.number().int(),
  proposedAt: z.string(),
  wonAt: z.string().nullable(),
  updatedAt: z.string(),
  ownerId: z.string().nullable(),
  ownerName: z.string().nullable(),
  contactName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  company: z.string().nullable(),
  piiRedacted: z.boolean(),
  landingName: z.string(),
  landingVersionNumber: z.number().int(),
});
export type DealSummary = z.infer<typeof dealSummarySchema>;

export const dealListSchema = z.object({
  items: z.array(dealSummarySchema),
  nextCursor: z.string().nullable(),
  counts: z.record(z.string(), z.number()),
  filteredCount: z.number().int(),
});
export type DealList = z.infer<typeof dealListSchema>;

export const pipelineStageSchema = z.object({
  id: z.string(), key: z.string(), name: z.string(), position: z.number().int(),
  active: z.boolean(), isEntry: z.boolean(), version: z.number().int(),
});
export type PipelineStage = z.infer<typeof pipelineStageSchema>;

export const createPipelineStageSchema = z.object({
  name: z.string().trim().min(1).max(80),
  position: z.number().int().min(0).max(10000).optional(),
});
export type CreatePipelineStage = z.infer<typeof createPipelineStageSchema>;

export const updatePipelineStageSchema = z.object({
  expectedVersion: z.number().int().min(0),
  name: z.string().trim().min(1).max(80).optional(),
  position: z.number().int().min(0).max(10000).optional(),
  active: z.boolean().optional(),
}).refine((value) => value.name !== undefined || value.position !== undefined || value.active !== undefined, 'Change at least one field');
export type UpdatePipelineStage = z.infer<typeof updatePipelineStageSchema>;

const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => !Number.isNaN(Date.parse(`${value}T00:00:00.000Z`)) && new Date(`${value}T00:00:00.000Z`).toISOString().slice(0, 10) === value, 'Use a valid date');
export const updateDealSchema = z.object({
  expectedVersion: z.number().int().min(0),
  title: z.string().trim().min(1).max(200).optional(),
  stageId: z.string().optional(),
  probability: z.number().int().min(0).max(100).optional(),
  expectedCloseDate: dateOnly.nullable().optional(),
  serviceKey: z.string().min(1).max(100).nullable().optional(),
  marketCode: z.string().min(1).max(20).nullable().optional(),
}).refine((value) => ['title', 'stageId', 'probability', 'expectedCloseDate', 'serviceKey', 'marketCode'].some((key) => value[key as keyof typeof value] !== undefined), 'Change at least one field');
export type UpdateDeal = z.infer<typeof updateDealSchema>;

export const dealOptionsSchema = z.object({
  services: z.array(z.object({ key: z.string(), name: z.string() })),
  markets: z.array(z.object({ code: z.string(), name: z.string() })),
});
export type DealOptions = z.infer<typeof dealOptionsSchema>;
